import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  // Number of numbered buttons to show around the current page (defaults to 1 on each side).
  siblingCount?: number;
  className?: string;
}

function buildPageRange(currentPage: number, totalPages: number, siblingCount: number): Array<number | 'gap'> {
  if (totalPages <= 1) return [1];

  const totalSlots = siblingCount * 2 + 5; // first + last + current + 2*siblings + 2 gaps
  if (totalPages <= totalSlots) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const leftSibling = Math.max(currentPage - siblingCount, 2);
  const rightSibling = Math.min(currentPage + siblingCount, totalPages - 1);
  const showLeftGap = leftSibling > 2;
  const showRightGap = rightSibling < totalPages - 1;

  const pages: Array<number | 'gap'> = [1];
  if (showLeftGap) pages.push('gap');
  for (let i = leftSibling; i <= rightSibling; i++) pages.push(i);
  if (showRightGap) pages.push('gap');
  pages.push(totalPages);
  return pages;
}

export default function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  siblingCount = 1,
  className = '',
}: PaginationProps) {
  const { t, i18n } = useTranslation('common');
  const isRTL = i18n.dir() === 'rtl';

  const pages = useMemo(
    () => buildPageRange(currentPage, totalPages, siblingCount),
    [currentPage, totalPages, siblingCount],
  );

  if (totalPages <= 1) return null;

  const onPrev = () => currentPage > 1 && onPageChange(currentPage - 1);
  const onNext = () => currentPage < totalPages && onPageChange(currentPage + 1);

  const baseBtn =
    'min-w-[40px] h-10 px-3 inline-flex items-center justify-center rounded-lg text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40';
  const inactiveBtn = 'bg-white border border-slate-200 text-slate-700 hover:border-[var(--pv-color-accent-soft)] hover:text-[#003930]';
  const activeBtn = 'bg-[#004D40] text-white border border-transparent';

  return (
    <nav
      className={`flex items-center justify-center gap-1.5 ${className}`}
      aria-label={t('pagination.label', { defaultValue: 'Pagination' })}
    >
      <button
        type="button"
        onClick={onPrev}
        disabled={currentPage === 1}
        className={`${baseBtn} ${inactiveBtn}`}
        aria-label={t('pagination.previous', { defaultValue: 'Previous page' })}
      >
        <i className={`ri-arrow-${isRTL ? 'right' : 'left'}-s-line text-lg`} />
      </button>

      {pages.map((p, idx) =>
        p === 'gap' ? (
          <span key={`gap-${idx}`} className="px-1 text-slate-400 select-none">
            …
          </span>
        ) : (
          <button
            key={p}
            type="button"
            onClick={() => onPageChange(p)}
            className={`${baseBtn} ${p === currentPage ? activeBtn : inactiveBtn}`}
            aria-current={p === currentPage ? 'page' : undefined}
          >
            {p}
          </button>
        ),
      )}

      <button
        type="button"
        onClick={onNext}
        disabled={currentPage === totalPages}
        className={`${baseBtn} ${inactiveBtn}`}
        aria-label={t('pagination.next', { defaultValue: 'Next page' })}
      >
        <i className={`ri-arrow-${isRTL ? 'left' : 'right'}-s-line text-lg`} />
      </button>
    </nav>
  );
}
